import React from 'react';
import Box from '@material-ui/core/Box';
import {
  Typography,
  Grid,
  Card,
  Button,
} from '@material-ui/core';
import AutocompleteMUI from '@mui/material/Autocomplete';
import MuiTextField from '@mui/material/TextField';
import { MLModelDeployProps } from './types';
import { useMLFlowStyles } from './styles';

export const MLModelDeploy = ({
  devices,
  selectedDevice,
  deviceInput,
  deploying,
  deployResult,
  isDeployDisabled,
  onDeviceInputChange,
  onDeviceChange,
  modelVersions,
  selectedVersion,
  versionInput,
  onVersionInputChange,
  onVersionChange,
  onDeploy,
}: MLModelDeployProps) => {
  const classes = useMLFlowStyles();

  const getCardClassName = () => {
    if (deployResult?.status === 'FAILED') {
      return classes.errorCard;
    }
    if (deployResult?.status === 'COMPLETED') {
      return classes.successCard;
    }
    return classes.infoCard;
  };

  return (
    <Box p={1}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={12}>
          <AutocompleteMUI
            options={devices}
            getOptionLabel={option => option.label}
            value={
              devices.find(d => d.value === selectedDevice) || null
            }
            inputValue={deviceInput}
            onInputChange={(_, newInput) => onDeviceInputChange(newInput)}
            onChange={(_, newValue) => onDeviceChange(newValue ? newValue.value : '')}
            renderInput={params => (
              <MuiTextField
                {...params}
                label="Registered Device"
                variant="standard"
                margin="dense"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={12}>
          <AutocompleteMUI
            options={modelVersions}
            getOptionLabel={option => option.label}
            value={
              modelVersions.find((o: any) => o.value === selectedVersion) || null
            }
            inputValue={versionInput}
            onInputChange={(_, newInput) => onVersionInputChange(newInput)}
            onChange={(_, newValue) => onVersionChange(newValue ? newValue.value : '')}
            renderInput={params => (
              <MuiTextField
                {...params}
                label="Model Version"
                variant="standard"
                margin="dense"
              />
            )}
          />
        </Grid>

      </Grid>

      {/* Deployment Result Message */}
      {deployResult && (
        <Box mt={2}>
          <Card className={getCardClassName()}>
            <Box p={1}>
              {/* Display accumulated logs */}
              {deployResult.logs && deployResult.logs.length > 0 && (
                <Box
                  mb={1}
                  className={classes.logSection}
                >
                  {deployResult.logs.map((log, index) => (
                    <Box key={`${log.timestamp}-${log.message}`} style={{ marginBottom: '2px' }}>
                      <span style={{ color: '#999', marginRight: '8px' }}>{index + 1}</span>
                      <span>{log.timestamp} </span>
                      <span style={{ color: log.message.includes('info:') ? '#22863a' : 'inherit' }}>
                        {log.message}
                      </span>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Display current status */}
              {deployResult.status === 'INITIATED' && (
                <Typography variant="body2" style={{ marginTop: '8px' }}>
                  <strong>Status:</strong> Deployment initiated successfully! Checking status in 15 seconds...
                </Typography>
              )}

              {deployResult.status === 'PENDING' && (
                <Typography variant="body2" style={{ marginTop: '8px' }}>
                  <strong>Status:</strong> Deployment is pending... Waiting for resources to be allocated...
                </Typography>
              )}

              {deployResult.status === 'IN_PROGRESS' && (
                <Typography variant="body2" style={{ marginTop: '8px' }}>
                  <strong>Status:</strong> Deployment in progress... Processing deployment tasks...
                </Typography>
              )}

              {deployResult.status === 'COMPLETED' && (
                <Box mt={1}>
                  <Typography variant="body2">
                    <strong>Status:</strong> Deployment completed successfully!
                  </Typography>
                </Box>
              )}

              {deployResult.status === 'FAILED' && (
                <Typography variant="body2" style={{ marginTop: '8px' }}>
                  <strong>Status:</strong> Deployment failed
                </Typography>
              )}

              {!deployResult.status && deployResult.message && (
                <Typography variant="body2">
                  {deployResult.message}
                </Typography>
              )}
            </Box>
          </Card>
        </Box>
      )}

      {/* Deploy Button */}
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={onDeploy}
          disabled={isDeployDisabled}
        >
          {deploying ? 'Deploying...' : 'Deploy Model'}
        </Button>
      </Box>
    </Box>
  );
};
